import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec3,
} from 'gl-matrix';
import { BoundingBox } from './BoundingBox';
import { Vertex } from './slicemesh/Vertex';
import { Edge } from './Edge';

/**
 * {@link RotatedBoundingBox} is a {@link BoundingBox} with some {@link ReadonlyMat3 rotation}.\
 * {@link BoundingBox} only represents a cuboid that is well aligned with the coordinate axes, and as such it doesn't scale very well.\
 * In many situations we would like to have {@link BoundingBox} that covers the data more accurately.
 *
 * For example if we only store a {@link BoundingBox} that is well aligned with the PatientSpace.\
 * That presentation is pretty sufficient as long as the data the {@link BoundingBox} represents has also a cuboid shape
 * and is well aligned with the PatientSpace.\
 * But if the data is not well aligned, then the {@link BoundingBox} is far from optimized.
 *
 * Also if we decide to give that underlying data some transformation (e.g. by applying registration to it),
 * the PatientSpace aligned {@link BoundingBox} has to be either re-generated or rotated.\
 * Both of those situations are somewhat cumbersome.
 */
export class RotatedBoundingBox extends BoundingBox {
    /**
     * The rotation that this {@link BoundingBox} should be rotated with.\
     * This is the rotation from ImageSpace to PatientSpace.
     */
    private _rotation: mat3;

    /**
     * Constructor that will initialize {@link RotatedBoundingBox} where\
     * {@link BoundingBox.min min} and {@link BoundingBox.max max} are undefined and
     * {@link _rotation} is identity.
     */
    public constructor();

    /**
     * Default constructor that creates a {@link RotatedBoundingBox} from the provided {@link BoundingBox} and {@link ReadonlyMat3 rotation}.
     * @param boundingBox The {@link BoundingBox} defining the boundaries
     * @param rotation Rotation {@link ReadonlyMat3 Matrix} that defines the rotation of the {@link BoundingBox}.\
     * This is the {@link ReadonlyMat3 rotation} from ImageSpace to PatientSpace.
     */
    public constructor(boundingBox: BoundingBox, rotation: ReadonlyMat3);

    /**
     * Regular copy constructor that creates a deep copy of a {@link RotatedBoundingBox}.
     * @param ref Reference to the {@link RotatedBoundingBox} that is to be copied.
     */
    public constructor(ref: RotatedBoundingBox);

    public constructor(
        boundingBox: BoundingBox | RotatedBoundingBox = new BoundingBox(),
        rotation?: ReadonlyMat3
    ) {
        super(boundingBox);
        if (boundingBox instanceof RotatedBoundingBox) {
            this._rotation = mat3.clone(boundingBox._rotation);
        } else {
            this._rotation = rotation ? mat3.clone(rotation) : mat3.create();
        }
    }

    /**
     * The {@link ReadonlyMat3 rotation} from ImageSpace to PatientSpace.
     * @returns the {@link _rotation}-variable stored in this object.
     */
    public get rotation(): ReadonlyMat3 {
        return this._rotation;
    }

    /**
     * Method to set the {@link _rotation rotation} of this {@link RotatedBoundingBox}.\
     * This should be the rotation from ImageSpace to PatientSpace.
     * @param rotation Rotation from ImageSpace to PatientSpace given to this {@link RotatedBoundingBox}.
     * @returns Reference to this
     */
    public rotate(rotation: ReadonlyMat3): RotatedBoundingBox {
        mat3.copy(this._rotation, rotation);
        return this;
    }

    /**
     * The {@link ReadonlyMat3 rotation} from PatientSpace to ImageSpace.\
     * This returns {@link mat3.transpose transpose} of the {@link _rotation} {@link ReadonlyMat3 matrix}.
     */
    public get invRotation(): ReadonlyMat3 {
        return mat3.transpose(mat3.create(), this._rotation);
    }

    /**
     * The centre of this box in PatientSpace. {@link center} is in the box's
     * own ImageSpace and still needs the {@link rotation} applied.
     */
    public get patientSpaceCenter(): ReadonlyVec3 {
        return vec3.transformMat3(vec3.create(), this.center, this._rotation);
    }

    /**
     * Whether any edge of this box crosses the projection plane.
     * @param toProjection Matrix taking PatientSpace to ProjectionSpace.
     */
    public intersectsProjection(toProjection: ReadonlyMat4): boolean {
        const textureEdges = this.getEdgesWithTexture();
        for (const edge of textureEdges) {
            if (edge.intersection(toProjection, textureEdges.length === 4)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Method to get the {@link vec3 coordinates} of all the 8 corners of the {@link BoundingBox}.\
     * Those coordinates are rotated by {@link rotation} to transform them into PatientSpace.
     * @returns Array of the {@link BoundingBox BoundingBox's} 8 vertex{@link vec3 coordinates} in PatientSpace.
     */
    public getVertices(
        additionalTransformation?: ReadonlyMat3 | ReadonlyMat4
    ): vec3[] {
        let transformation: mat3 | mat4 = mat3.clone(this.rotation);
        if (additionalTransformation) {
            if (additionalTransformation.length === 9) {
                mat3.multiply(
                    transformation,
                    additionalTransformation,
                    transformation
                );
            } else {
                const rotAs4by4 = mat4.fromValues(
                    transformation[0],
                    transformation[1],
                    transformation[2],
                    0,
                    transformation[3],
                    transformation[4],
                    transformation[5],
                    0,
                    transformation[6],
                    transformation[7],
                    transformation[8],
                    0,
                    0,
                    0,
                    0,
                    1
                );
                transformation = mat4.multiply(
                    rotAs4by4,
                    additionalTransformation as ReadonlyMat4,
                    rotAs4by4
                );
            }
        }
        return super.getVertices(transformation);
    }

    public toPatientSpace(vector: vec3): vec3 {
        const planarTranslation = vec3.clone(super.center);
        planarTranslation[2] = 0;
        vec3.transformMat3(planarTranslation, planarTranslation, this.rotation);
        const planarSize = vec3.clone(this.size);
        planarSize[2] = 1;
        const transformation = mat4.fromRotationTranslationScale(
            mat4.create(),
            this.rotation,
            planarTranslation,
            planarSize
        );
        vec3.transformMat4(vector, vector, transformation);
        return vector;
    }

    public toImageSpace(vector: vec3): vec3 {
        const planarTranslation = vec3.clone(super.center);
        planarTranslation[2] = 0;
        vec3.transformMat3(planarTranslation, planarTranslation, this.rotation);
        const planarSize = vec3.clone(this.size);
        planarSize[2] = 1;
        const transformation = mat4.fromRotationTranslationScale(
            mat4.create(),
            this.rotation,
            planarTranslation,
            planarSize
        );
        const invTransformation = mat4.invert(mat4.create(), transformation);
        return vec3.transformMat4(vector, vector, invTransformation);
    }

    /**
     * @returns A matrix transforming points from TextureSpace [-1, 1] to PatientSpace
     */
    public textureToPatientSpaceTransformation(): ReadonlyMat4 {
        // Move the corner from -1,-1,-1 to 0,0,0 and scale to size
        return mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.fromMat3(quat.create(), this.rotation),
            this.patientSpaceCenter,
            vec3.scale(vec3.create(), this.size, 0.5)
        );
    }

    /**
     * @returns A matrix transforming points from PatientSpace to TextureSpace [-1, 1]
     */
    public patientToTextureSpaceTransformation(): ReadonlyMat4 {
        const textureToPatient = this.textureToPatientSpaceTransformation();
        const transformation = mat4.invert(mat4.create(), textureToPatient);
        return transformation;
    }

    public toAxisAlignedBoundingBox(): BoundingBox {
        const vertices = this.getVertices();
        const aabb = new BoundingBox();
        for (const vertex of vertices) {
            aabb.expandWithPoint(vertex);
        }
        return aabb;
    }

    /**
     * Method to get the 8 {@link vertex Vertices} of the {@link BoundingBox}.\
     * Those {@link Vertex Vertices} have {@link vec3 PatientSpaceCoordinates} and {@link ReadonlyVec3 textureCoordinates}.\
     * If {@link additionalTransformation}-parameter is provided, those {@link Vertex Vertices} are {@link Vertex.transform transformed} with that.
     * @param additionalTransformation Optional {@link ReadonlyMat3} or {@link ReadonlyMat4} transformation to be applied to all {@link Vertex Vertices}.
     * @returns Array of the {@link BoundingBox BoundingBox's} 8 {@link Vertex Vertices} rotated and transformed.
     */
    public getVerticesWithTexture(
        additionalTransformation?: ReadonlyMat3 | ReadonlyMat4
    ): Vertex[] {
        let transformation: mat3 | mat4 = mat3.clone(this.rotation);
        if (additionalTransformation) {
            if (additionalTransformation.length === 9) {
                mat3.multiply(
                    transformation,
                    additionalTransformation,
                    transformation
                );
            } else {
                const rotAs4by4 = mat4.fromValues(
                    transformation[0],
                    transformation[1],
                    transformation[2],
                    0,
                    transformation[3],
                    transformation[4],
                    transformation[5],
                    0,
                    transformation[6],
                    transformation[7],
                    transformation[8],
                    0,
                    0,
                    0,
                    0,
                    1
                );
                transformation = mat4.multiply(
                    rotAs4by4,
                    additionalTransformation as ReadonlyMat4,
                    rotAs4by4
                );
            }
        }
        return super.getVerticesWithTexture(transformation);
    }

    /**
     * Method to get the Edges of this {@link RotatedBoundingBox}.\
     * These edges are defined by two {@link vec3 points} in PatientSpace.\
     * These points may be transformed by {@link additionalTransformation} before the edges are computed.
     * @param additionalTransformation Optional {@link ReadonlyMat3} or {@link ReadonlyMat4} transformation to be applied to the {@link vec3 points} before the edges are computed.
     * @returns An {@link Array} containing the 12 edges of this {@link RotatedBoundingBox},\
     * or an empty array if the bounding box has no valid bounds.
     */
    public getEdges(
        additionalTransformation?: ReadonlyMat3 | ReadonlyMat4
    ): { start: ReadonlyVec3; end: ReadonlyVec3 }[] {
        const vertices = this.getVertices(additionalTransformation);
        if (vertices.length !== 8) {
            return [];
        }
        const edges = new Array<{ start: ReadonlyVec3; end: ReadonlyVec3 }>();
        for (let i = 0; i < 4; ++i) {
            edges.push({ start: vertices[i], end: vertices[(i + 1) % 4] });
            edges.push({
                start: vertices[i + 4],
                end: vertices[((i + 1) % 4) + 4],
            });
            edges.push({ start: vertices[i], end: vertices[i + 4] });
        }
        return edges;
    }

    /**
     * Method to get the textured {@link Edge Edges} of this {@link RotatedBoundingBox}.\
     * These {@link Edge Edges} are defined by two {@link Vertex Vertices} in PatientSpace.\
     * These {@link Vertex Vertices} may be {@link Vertex transformed} by {@link additionalTransformation} before the {@link Edge Edges} are generated.
     * @param additionalTransformation Optional {@link ReadonlyMat3} or {@link ReadonlyMat4} transformation to be applied to the {@link Vertex Vertices} before the {@link Edge Edges} are generated.
     * @returns An {@link Array} containing the 12 {@link Edge Edges} of this {@link RotatedBoundingBox}.
     */
    public getEdgesWithTexture(
        additionalTransformation?: ReadonlyMat3 | ReadonlyMat4
    ): Edge[] {
        const vertices = this.getVerticesWithTexture(additionalTransformation);
        if (vertices.length !== 8) {
            return [];
        }
        const edges = new Array<Edge>();
        // bottom edges
        for (let i = 0; i < 4; ++i) {
            edges.push(new Edge(vertices[i], vertices[(i + 1) % 4]));
        }
        // vertical edges
        for (let i = 0; i < 4; ++i) {
            edges.push(new Edge(vertices[i], vertices[i + 4]));
        }
        // top edges
        for (let i = 0; i < 4; ++i) {
            edges.push(new Edge(vertices[i + 4], vertices[((i + 1) % 4) + 4]));
        }
        return edges;
    }

    /**
     * Method to find the intersection points between an {@link edge} and this {@link RotatedBoundingBox}.\
     * The {@link edge} is defined by two {@link vec3 points} in PatientSpace.\
     * The {@link edge} is transformed by {@link transformation} before the intersection is computed.
     *
     * It computes the intersection point between the edge with every face of the {@link RotatedBoundingBox}.\
     * If the intersection point is within the boundaries of the {@link RotatedBoundingBox}, it is returned.\
     * Otherwise null is returned.
     *
     * @param edge Vector defined by two {@link ReadonlyVec3 points} in PatientSpace.
     * @param transformation {@link ReadonlyMat3} or {@link ReadonlyMat4} that defines the transformation of the {@link edge} from PatientSpace to ImageSpace.
     * @returns The {@link ReadonlyVec3} intersection point if it is within the boundaries of the {@link RotatedBoundingBox}, null otherwise.
     */
    public edgeIntersection(
        edge: { start: ReadonlyVec3; end: ReadonlyVec3 },
        transformation?: ReadonlyMat3 | ReadonlyMat4
    ): ReadonlyVec3[] {
        const intersections: ReadonlyVec3[] = [];
        if (this.bounds) {
            if (transformation) {
                if (transformation.length === 9) {
                    edge = {
                        start: vec3.transformMat3(
                            vec3.create(),
                            edge.start,
                            transformation
                        ),
                        end: vec3.transformMat3(
                            vec3.create(),
                            edge.end,
                            transformation
                        ),
                    };
                } else {
                    edge = {
                        start: vec3.transformMat4(
                            vec3.create(),
                            edge.start,
                            transformation as ReadonlyMat4
                        ),
                        end: vec3.transformMat4(
                            vec3.create(),
                            edge.end,
                            transformation as ReadonlyMat4
                        ),
                    };
                }
            }
            for (let i = 0; i < 3; ++i) {
                let dist =
                    (this.bounds.min[i] - edge.start[i]) /
                    (edge.end[i] - edge.start[i]);
                if (dist >= 0 && dist <= 1) {
                    const intersection = vec3.subtract(
                        vec3.create(),
                        edge.end,
                        edge.start
                    );
                    vec3.scale(intersection, intersection, dist);
                    vec3.add(intersection, edge.start, intersection);

                    //check if intersection is within the other two dimensions
                    if (
                        intersection[(i + 1) % 3] >=
                            this.bounds.min[(i + 1) % 3] &&
                        intersection[(i + 1) % 3] <=
                            this.bounds.max[(i + 1) % 3] &&
                        intersection[(i + 2) % 3] >=
                            this.bounds.min[(i + 2) % 3] &&
                        intersection[(i + 2) % 3] <=
                            this.bounds.max[(i + 2) % 3]
                    ) {
                        intersections.push(intersection);
                    }
                }
                dist =
                    (this.bounds.max[i] - edge.start[i]) /
                    (edge.end[i] - edge.start[i]);
                if (dist >= 0 && dist <= 1) {
                    const intersection = vec3.subtract(
                        vec3.create(),
                        edge.end,
                        edge.start
                    );
                    vec3.scale(intersection, intersection, dist);
                    vec3.add(intersection, edge.start, intersection);

                    //check if intersection is within the other two dimensions
                    if (
                        intersection[(i + 1) % 3] >=
                            this.bounds.min[(i + 1) % 3] &&
                        intersection[(i + 1) % 3] <=
                            this.bounds.max[(i + 1) % 3] &&
                        intersection[(i + 2) % 3] >=
                            this.bounds.min[(i + 2) % 3] &&
                        intersection[(i + 2) % 3] <=
                            this.bounds.max[(i + 2) % 3]
                    ) {
                        intersections.push(intersection);
                    }
                }
            }
        }
        return intersections;
    }

    public addMargins(
        xMin: number | ReadonlyVec3,
        yMin?: number | ReadonlyVec3,
        zMin?: number,
        xMax?: number,
        yMax?: number,
        zMax?: number
    ): RotatedBoundingBox {
        if (xMin instanceof Object) {
            if (yMin) {
                super.addMargins(xMin, yMin as ReadonlyVec3);
            } else {
                super.addMargins(xMin);
            }
        } else {
            if (yMin === undefined) {
                super.addMargins(xMin);
            } else if (xMax === undefined) {
                super.addMargins(xMin, yMin as number, zMin as number);
            } else {
                super.addMargins(
                    xMin,
                    yMin as number,
                    zMin as number,
                    xMax,
                    yMax as number,
                    zMax as number
                );
            }
        }
        return this;
    }

    /**
     * @override Overrides the {@link BoundingBox.contains}-method.\
     * Method to check if this {@link RotatedBoundingBox} covers another {@link RotatedBoundingBox}.\
     * Meaning that all the {@link BoundingBox.getVertices vertices} of the {@link ref} reside within the boundaries of {@link RotatedBoundingBox this}.
     * @param ref {@link RotatedBoundingBox} which should be contained within this {@link RotatedBoundingBox}.
     * @returns True if every {@link BoundingBox.getVertices vertex} of {@link ref} is within {@link RotatedBoundingBox this}, false otherwise.
     */
    public contains(ref: RotatedBoundingBox): boolean {
        if (
            !this.bounds ||
            !ref.bounds ||
            !mat3.equals(this.rotation, ref.rotation)
        ) {
            return false;
        }

        const min = vec3.transformMat3(
            vec3.create(),
            this.bounds.min,
            this.rotation
        );
        const max = vec3.transformMat3(
            vec3.create(),
            this.bounds.max,
            this.rotation
        );

        const minRef = vec3.transformMat3(
            vec3.create(),
            ref.bounds.min,
            this.rotation
        );
        const maxRef = vec3.transformMat3(
            vec3.create(),
            ref.bounds.max,
            this.rotation
        );

        for (let i = 0; i < 3; ++i) {
            if (
                min[i] > minRef[i] ||
                max[i] < maxRef[i] ||
                min[i] > maxRef[i] ||
                max[i] < minRef[i]
            ) {
                return false;
            }
        }

        return true;
    }

    /**
     * Method to compute the {@link RotatedBoundingBox} that covers the intersection of two {@link RotatedBoundingBox RotatedBoundingBoxes}.\
     * As the intersection between two {@link RotatedBoundingBox RotatedBoundingBoxes} may be a complex shape,\
     * the {@link rotation}-parameter is used to define the {@link RotatedBoundingBox.rotation rotation} of the {@link RotatedBoundingBox} that covers the intersection.
     * @param rotation {@link ReadonlyMat3} that defines the rotation of the resulting {@link RotatedBoundingBox}. So rotation from ImageSpace to PatientSpace.
     * @param bb1 {@link RotatedBoundingBox} that should be intersected with {@link bb2}.
     * @param bb2 {@link RotatedBoundingBox} that should be intersected with {@link bb1}.
     * @returns A {@link RotatedBoundingBox} whose rotation is {@link rotation}, that covers the intersection in between {@link bb1} and {@link bb2}, or null if they don't intersect.
     */
    public static rotatedIntersection(
        rotation: ReadonlyMat3,
        bb1: RotatedBoundingBox,
        bb2: RotatedBoundingBox
    ): RotatedBoundingBox | null {
        /**
         * {@link Array} of {@link vec3 points} within PatientSpace that define the intersection between {@link bb1} and {@link bb2}.
         *
         * There are two types of points that may define the intersection:
         * 1. One of the {@link BoundingBox.getVertices vertices} of {@link RotatedBoundingBox} that reside within the other {@link RotatedBoundingBox}.
         * 2. Intersection between the edges of one {@link RotatedBoundingBox} with the faces of the other {@link RotatedBoundingBox}.
         */
        const points = new Array<vec3>();
        const toBb2 = bb2.invRotation;
        for (const v of bb1.getVertices()) {
            if (
                bb2.isPointInside(vec3.transformMat3(vec3.create(), v, toBb2))
            ) {
                points.push(v);
            }
        }
        const toBb1 = bb1.invRotation;
        for (const v of bb2.getVertices()) {
            if (
                bb1.isPointInside(vec3.transformMat3(vec3.create(), v, toBb1))
            ) {
                points.push(v);
            }
        }
        for (const edge of bb1.getEdges(toBb2)) {
            const intersections = bb2.edgeIntersection(edge);
            for (const intersection of intersections) {
                points.push(
                    vec3.transformMat3(
                        vec3.create(),
                        intersection,
                        bb2.rotation
                    )
                );
            }
        }
        for (const edge of bb2.getEdges(toBb1)) {
            const intersections = bb1.edgeIntersection(edge);
            for (const intersection of intersections) {
                points.push(
                    vec3.transformMat3(
                        vec3.create(),
                        intersection,
                        bb1.rotation
                    )
                );
            }
        }
        if (points.length === 0) {
            return null;
        } else {
            const result = new RotatedBoundingBox();
            result.rotate(rotation);
            for (const point of points) {
                result.expandWithPoint(
                    vec3.transformMat3(vec3.create(), point, result.invRotation)
                );
            }
            return result;
        }
    }
}
